import { useWizardStore } from '@/store/wizardStore'
import type { ChargingMethod } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step7AutonomyProps { lang: Lang }

const COPY = {
  fr: {
    hoursLabel: 'Heures d\'opération par jour',
    hoursHint: 'Durée cible de fonctionnement continu par cycle de 24h',
    chargingLabel: 'Méthode de recharge',
    autoDock: 'Station de charge automatique',
    autoDockDesc: 'Le robot rejoint sa base dès que nécessaire sans intervention.',
    manual: 'Recharge manuelle',
    manualDesc: 'Un opérateur connecte le robot à la borne à la fin du cycle.',
  },
  en: {
    hoursLabel: 'Operating hours per day',
    hoursHint: 'Target continuous operation time per 24h cycle',
    chargingLabel: 'Charging method',
    autoDock: 'Automatic docking station',
    autoDockDesc: 'Robot returns to base whenever needed without intervention.',
    manual: 'Manual charging',
    manualDesc: 'An operator connects the robot to the dock at end of cycle.',
  },
}

export default function Step7Autonomy({ lang }: Step7AutonomyProps) {
  const { draft, update } = useWizardStore()
  const t = COPY[lang]
  const hours = draft.step7_hours_per_day ?? 8
  const charging = draft.step7_charging

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-3xl">
      {/* Hours slider */}
      <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
        <div className="flex items-center gap-3 mb-2">
          <span className="material-symbols-outlined text-primary text-2xl">schedule</span>
          <span className="font-headline text-base font-bold text-on-surface">{t.hoursLabel}</span>
        </div>
        <p className="text-xs text-on-surface-variant mb-6 font-label">{t.hoursHint}</p>
        <div className="font-headline text-5xl font-bold text-primary-container mb-1">
          {hours}
          <span className="text-xl text-on-surface-variant ml-2 font-normal">h</span>
        </div>
        <div className="text-xs text-on-surface-variant font-label mb-4">
          {lang === 'fr' ? `≈ ${(hours / 24 * 100).toFixed(0)}% du cycle journalier` : `≈ ${(hours / 24 * 100).toFixed(0)}% of daily cycle`}
        </div>
        <input
          type="range"
          min={2}
          max={24}
          step={1}
          value={hours}
          onChange={(e) => update({ step7_hours_per_day: Number(e.target.value) })}
          className="w-full accent-primary-container"
        />
        <div className="flex justify-between text-xs text-on-surface-variant mt-1 font-label">
          <span>2h</span>
          <span>24h</span>
        </div>
      </div>

      {/* Charging method */}
      <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
        <div className="flex items-center gap-3 mb-6">
          <span className="material-symbols-outlined text-secondary text-2xl">bolt</span>
          <span className="font-headline text-base font-bold text-on-surface">{t.chargingLabel}</span>
        </div>
        <div className="space-y-4">
          {(['auto_dock', 'manual'] as ChargingMethod[]).map((method) => {
            const isSelected = charging === method
            const title = method === 'auto_dock' ? t.autoDock : t.manual
            const desc = method === 'auto_dock' ? t.autoDockDesc : t.manualDesc
            const icon = method === 'auto_dock' ? 'ev_station' : 'power'
            return (
              <div
                key={method}
                onClick={() => update({ step7_charging: method })}
                className={`p-5 rounded-xl border cursor-pointer transition-all flex items-start gap-4 ${isSelected ? 'border-secondary/60 bg-secondary/10' : 'border-outline-variant/20 hover:border-outline-variant/40'}`}
              >
                <span className={`material-symbols-outlined text-2xl mt-0.5 ${isSelected ? 'text-secondary' : 'text-on-surface-variant'}`}>{icon}</span>
                <div>
                  <div className={`font-headline text-sm font-bold mb-1 ${isSelected ? 'text-secondary' : 'text-on-surface'}`}>{title}</div>
                  <div className="text-xs text-on-surface-variant font-label leading-relaxed">{desc}</div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
